'use client';
import { useEffect, useState } from 'react';

import { show as showIntercom } from '@intercom/messenger-js-sdk';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/modules/common/components/ui/Accordion';
import { slugify } from '@/modules/common/utils/string';
import { LandingPageItemFragment } from '@/modules/landing-page/gql/__generated/landingPage.generated';

import { useSeoMetaDataBySlugQuery } from '../gql/__generated/seoMetadata.generated';
import { CTFJson, parseNodeContentIntoHtml } from '../lib/contentfull';

type FAQTopic = {
  title: string;
  id: string;
  questions: {
    question: string;
    answer: string;
  }[];
};

type FAQType = {
  title: string;
  descriptions: string;
  topics: FAQTopic[];
};

function parseFAQ(jsonData: LandingPageItemFragment): FAQType | null {
  const blocks = jsonData?.pageContentCollection?.items;

  if (!blocks) {
    return null;
  }

  const headerContent = blocks.find((block) => block?.internalName === 'header')?.text?.json
    ?.content;

  const title = headerContent?.find((node: CTFJson) => node.nodeType === 'heading-1')?.content?.[0]
    .value;
  const descriptions = headerContent?.find((node: CTFJson) => node.nodeType === 'paragraph')
    ?.content?.[0].value;

  const faqBlocks = blocks.filter((block) => block?.internalName?.startsWith('faq-list'));
  const topics: FAQTopic[] = [];

  faqBlocks.forEach((block) => {
    const topicTitle = block?.title;

    if (!topicTitle) {
      return;
    }

    const topicId = slugify(topicTitle);
    const questions: { question: string; answer: string }[] = [];

    let currentQuestion = '';
    let currentAnswer = '';

    block?.text?.json?.content.forEach((node: CTFJson) => {
      if (node.nodeType === 'heading-2') {
        return;
      }

      if (node.nodeType === 'heading-3') {
        if (currentQuestion && currentAnswer) {
          questions.push({ question: currentQuestion, answer: currentAnswer });
        }
        currentQuestion = (node.content ?? []).map((textNode: CTFJson) => textNode.value).join('');
        currentAnswer = '';
      } else {
        currentAnswer += parseNodeContentIntoHtml(node);
      }
    });

    if (currentQuestion && currentAnswer) {
      questions.push({ question: currentQuestion, answer: currentAnswer });
    }

    if (questions.length > 0) {
      topics.push({
        title: topicTitle,
        id: topicId,
        questions,
      });
    }
  });

  return {
    title,
    descriptions,
    topics,
  };
}

type FAQPageTemplateType = {
  pageContent: LandingPageItemFragment;
};

export function FAQPageTemplate({ pageContent }: FAQPageTemplateType) {
  const [isJavaScriptLoaded, setIsJavaScriptLoaded] = useState(false);
  const faqData = parseFAQ(pageContent);

  // Enable accordion behavior after component mounts (JavaScript is available)
  useEffect(() => {
    setIsJavaScriptLoaded(true);
  }, []);

  // Intercept clicks on contact-us links in FAQ answers
  useEffect(() => {
    // Delegate click handler for all FAQ answers
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.closest) {
        const link = target.closest('a[href]');
        if (link) {
          const href = link.getAttribute('href') || '';
          // Accept relative and absolute URLs for contact-us
          if (
            href === '/contact-us' ||
            href === 'contact-us' ||
            href.match(/^https?:\/\/(www\.)?designshop\.com\/contact-us(\/)?(\?.*)?$/)
          ) {
            e.preventDefault();
            showIntercom();
          }
        }
      }
    };
    // Attach to the FAQ answers container
    const faqContainer = document.getElementById('faq-answers-root');
    if (faqContainer) {
      faqContainer.addEventListener('click', handleClick);
    }
    return () => {
      if (faqContainer) {
        faqContainer.removeEventListener('click', handleClick);
      }
    };
  }, [faqData]);

  useEffect(() => {
    const handleScroll = () => {
      if (!faqData?.topics.length) {
        return;
      }

      const sections = faqData.topics.map((topic) => {
        const element = document.getElementById(topic.id);
        if (!element) {
          return { id: topic.id, position: 0 };
        }

        const rect = element.getBoundingClientRect();
        const position = Math.abs(rect.top);
        return { id: topic.id, position };
      });

      sections.sort((a, b) => a.position - b.position);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [faqData]);

  const { data } = useSeoMetaDataBySlugQuery({
    slug: 'faq',
  });

  const seoData = data?.seoMetadataCollection?.items[0];

  const faqSchema = seoData?.schema ?? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity:
      faqData?.topics.flatMap((topic) =>
        topic.questions.map((item) => ({
          '@type': 'Question',
          name: item.question.replace(/<\/?h3>/g, ''),
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer.replace(/<\/?p>/g, '').slice(0, 200),
          },
        }))
      ) || [],
  };

  if (!faqData) {
    throw new Error('FAQ data is missing');
  }

  const handleTopicClick = (topicId: string) => {
    const element = document.getElementById(topicId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth',
      });
    }
  };

  return (
    <main className="mx-auto my-8 max-w-[1200px] px-4 lg:my-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <header className="mb-4 lg:mb-10">
        <h1
          className="mx-auto mb-4 text-[28px] font-bold leading-none lg:text-4xl"
          data-testid="title"
        >
          {faqData.title}
        </h1>
        <p className="leading-snug text-gray-450" data-testid="subtitle">
          {faqData.descriptions}
        </p>
      </header>

      <div className="flex w-full flex-col lg:flex-row lg:gap-28">
        {faqData.topics.length > 1 && (
          <aside className="mb-5 h-fit w-64 flex-shrink-0 self-start pr-8 lg:sticky lg:top-24">
            <nav aria-label="FAQ Topics">
              <p className="mb-3 text-gray-450">Topics</p>
              <ul className="space-y-2">
                {faqData.topics.map((topic) => (
                  <li key={topic.id}>
                    <button onClick={() => handleTopicClick(topic.id)} className="font-bold">
                      {topic.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        )}

        <div className="flex-grow" id="faq-answers-root">
          {faqData.topics.map((topic, topicIndex) => (
            <section key={topic.id} id={topic.id} className="mb-12 scroll-mt-24">
              <h2 className="py-2 text-2xl font-bold lg:py-5 lg:text-[28px]">{topic.title}</h2>
              {
                isJavaScriptLoaded ?
                  <Accordion type="single" collapsible defaultValue={`${topicIndex}-0`}>
                    {topic.questions.map((item, index) => {
                      return (
                        <AccordionItem key={index} value={`${topicIndex}-${index}`}>
                          <AccordionTrigger className="border-b border-gray-75">
                            <div
                              className="py-3 text-left font-bold"
                              dangerouslySetInnerHTML={{ __html: item.question }}
                            />
                          </AccordionTrigger>
                          <AccordionContent>
                            <div
                              className="rtf-content py-4 text-base"
                              dangerouslySetInnerHTML={{ __html: item.answer }}
                            />
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                  // Fallback: Always open content when JavaScript is not available
                : <div className="space-y-4">
                    {topic.questions.map((item, index) => (
                      <div key={index} className="border-b border-gray-75">
                        <div
                          className="py-3 text-left font-bold"
                          dangerouslySetInnerHTML={{ __html: item.question }}
                        />
                        <div
                          className="rtf-content py-4 text-base"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </div>
                    ))}
                  </div>

              }
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}

export default FAQPageTemplate;
